import { inject, computed } from "@angular/core";
import {
    signalStore,
    withState,
    withComputed,
    withMethods,
    patchState,
    withHooks,
} from "@ngrx/signals";
import { rxMethod } from "@ngrx/signals/rxjs-interop";
import { BackOfficeOfferViewModel } from "@playcharge/common/models/backend/offers/backoffice/backoffice-offer-view-model";
import { OfferType } from "@playcharge/common/models/backend/offers/offer-type";
import { UpdateOfferEditModel } from "@playcharge/common/models/backend/offers/backoffice/update-offer-edit-model";
import { UpdateOfferGoalsEditModel } from "@playcharge/common/models/backend/offers/backoffice/update-offer-goals-edit-model";
import { Observable, tap, pipe, filter, switchMap } from "rxjs";
import { ApiOffersService } from "../../services/api-offers.service";
import {
    withRequestStatus,
    setPending,
    setFulfilled,
    setError,
} from "../extensions/request-status.feature";
import { withRouteParams } from "../extensions/route-params.feature";
import { AppsStore } from "./apps.store";
import { GamesStore } from "./games.store";
import { UpdateOfferChallengeBlocksEditModel } from "@playcharge/common/models/backend/offers/backoffice/update-offer-challenge-blocks-edit-model";

export type OfferStore = InstanceType<typeof OfferStore>;

export const OfferStore = signalStore(
    { protectedState: false },
    withRouteParams({ offerId: (param) => Number(param) }),
    withRequestStatus(),
    withComputed(({ offerId }, gamesStore = inject(GamesStore), appsStore = inject(AppsStore)) => ({
        offer: computed(() =>
            offerId()
                ? gamesStore.entityMap()[offerId()] || appsStore.entityMap()[offerId()]
                : null,
        ),
    })),
    withMethods(
        (
            store,
            gamesStore = inject(GamesStore),
            appsStore = inject(AppsStore),
            apiOffersService = inject(ApiOffersService),
        ) => ({
            updateOffer(offer: UpdateOfferEditModel): Observable<BackOfficeOfferViewModel> {
                return apiOffersService.updateOffer(offer).pipe(
                    tap((offer) => {
                        switch (offer.offerType) {
                            case OfferType.Game:
                                gamesStore.setOffer(offer);
                                break;
                            case OfferType.App:
                                appsStore.setOffer(offer);
                                break;
                        }
                    }),
                );
            },
            updateOfferGoals(goals: UpdateOfferGoalsEditModel) {
                return apiOffersService.updateOfferGoals(store.offerId(), goals).pipe(
                    tap((offer) => {
                        switch (offer.offerType) {
                            case OfferType.Game:
                                gamesStore.setOffer(offer);
                                break;
                            case OfferType.App:
                                appsStore.setOffer(offer);
                                break;
                        }
                    }),
                );
            },
            updateOfferChallengeBlocks(
                model: UpdateOfferChallengeBlocksEditModel,
            ): Observable<BackOfficeOfferViewModel> {
                return apiOffersService.updateOfferChallengeBlocks(store.offerId(), model);
            },
            loadOffer: rxMethod<number>(
                pipe(
                    filter((id) => !store.offer()),
                    tap(() => patchState(store, setPending())),
                    switchMap((id) => {
                        return apiOffersService.getById(id).pipe(
                            tap({
                                next: (offer) => {
                                    const store =
                                        offer.offerType === OfferType.Game ? gamesStore : appsStore;

                                    patchState(store, setFulfilled());
                                    store.setOffer(offer);
                                },
                                error: (error) => patchState(store, setError(error.error.error)),
                            }),
                        );
                    }),
                ),
            ),
        }),
    ),
    withHooks({
        onInit({ loadOffer, offerId }) {
            loadOffer(offerId);
        },
    }),
);
